import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  resetOrder,
  setPhysicalAddressRecipient,
  removePhysicalAddressRecipient,
  setCartItemRecipient,
  removeCartItemRecipient,
  createCashOrder,
  setStepId,
} from '../../../../actions/cashActions';
import AddEditCart from '../../components/add-edit-cart/add-edit-cart';
import OrderConfirmationSummaryPage from '../../components/order-confirmation-summary-page/order-confirmation-summary-page';
import DeliveryOptions from '../../components/delivery-options/delivery-options';
import AmpolCashRecipientForm from '../../components/ampol-cash-recipient-form/ampol-cash-recipient-form';
import { ButtonVariant, useModal } from '@ampol/design-system';
import ModalContentWrapper from 'components/modal-content-wrapper/modal-content-wrapper';
import { getCreateOrderRequest } from '../../helpers/cash-order-helper';
import PageLoader from '../../../../components/page-loader/page-loader';
import dayjs from 'dayjs';
import { addToastAlert, resetToastAlert } from 'actions/toastAlertActions';
import Alert from 'constants/alert-messages';
import { trackCustomEvent } from 'utilities/app-insights';

import { useAmpolCashOrderSelection } from './hooks/useAmpolCashOrderSelection';
import { useAmpolCashOrderValidation } from './hooks/useAmpolCashOrderValidation';
import { useAmpolCashOrderGTM } from './hooks/useAmpolCashOrderGTM';
import { useAmpolCashOrderActions } from './hooks/useAmpolCashOrderActions';
import { selectAmpolCashOrderModalContext } from 'selectors/ampolCashOrderModalSelectors';

export const steps = {
  Start: 1,
  DeliveryOptions: 2,
  AddEditRecipient: 3,
  Summary: 4,
  OrderPlaced: 5,
};

const AmpolCashOrderModal = ({ onClose, onCashOrderSubmitted }) => {
  const dispatch = useDispatch();

  const {
    assignedGrandfatherClientNumber,
    loggedInUser,
    assignedAccountNumber,
    cashOrderDefaults,
    cashOrderDefaultsLoading,
    cashOrderDefaultsError,
    plasticTypesListLoading,
    plasticTypesListError,
    cashOrderCartItems,
    cashCartSubTotal,
    deliveryOption,
    singleShippingAddress,
    physicalAddressRecipient,
    isAdmin,
    shippingMethod,
    isFeeWaiveOverride,
    promoDiscount,
    requestedName,
    requestedPhone,
    caseNumber,
    purchaseOrderNumber,
    createCashOrderLoading,
    createCashOrderResponse,
    stepId,
  } = useSelector(selectAmpolCashOrderModalContext);

  // Ref to keep the current step's onNextClick fresh for effects
  const currentStepRef = useRef(null);

  // Selection state management hook
  const {
    formId,
    setFormId,
    isCartItemsValid,
    setIsCartItemsValid,
    hasCartItems,
    cartItemUuid,
    setCartItemUuid,
    recipientUuid,
    setRecipientUuid,
    isDeliveryOptionsValid,
    setIsDeliveryOptionsValid,
  } = useAmpolCashOrderSelection(cashOrderCartItems);

  const deliveryOptionsFormId = 'ampol-cash-orders-delivery-options-form';
  const addEditRecipientFormId = 'ampol-cash-orders-add-edit-recipient-form';
  const summaryFormId = 'ampol-cash-orders-summary-form';

  // GTM tracking hook
  const { trackCloseModal, trackContinueToDeliveryOptions, trackContinueToSummary, trackPlaceOrder, trackOrderPlaced } =
    useAmpolCashOrderGTM(loggedInUser, assignedAccountNumber);

  // Validation hook
  const { showDeliveryErrors, onDeliveryOptionsChange, onDeliveryOptionsContinue } = useAmpolCashOrderValidation(
    deliveryOption,
    isDeliveryOptionsValid,
    setIsDeliveryOptionsValid
  );

  // Actions hook - initially without cancelConfirmation
  const {
    onRecipientSubmit,
    onRecipientsAddClick,
    onRecipientsEditClick,
    onRecipientsRemoveClick,
    closeModal,
    confirmClose: confirmCloseFromHook,
    onAddEditAmpolCashClick,
    onPlaceOrder,
  } = useAmpolCashOrderActions({
    assignedGrandfatherClientNumber,
    cashOrderCartItems,
    cashOrderDefaults,
    deliveryOption,
    singleShippingAddress,
    physicalAddressRecipient,
    shippingMethod,
    isFeeWaiveOverride,
    promoDiscount,
    requestedName,
    requestedPhone,
    caseNumber,
    purchaseOrderNumber,
    cartItemUuid,
    setCartItemUuid,
    recipientUuid,
    setRecipientUuid,
    setFormId,
    deliveryOptionsFormId,
    addEditRecipientFormId,
    summaryFormId,
    trackCloseModal,
    trackPlaceOrder,
    onClose,
    cancelConfirmation: null,
  });

  const cancelConfirmation = useModal({
    id: 'confirm-cancellation',
    title: 'Cancel order',
    content: 'Are you sure you want to cancel this order?',
    color: 'red',
    actions: [
      {
        label: 'Yes',
        onClick: closeModal,
      },
      {
        label: 'No',
        onClick: () => {
          if (!isDeliveryOptionsValid) {
            showDeliveryErrors(deliveryOption, steps.DeliveryOptions);
          }
          cancelConfirmation.close();
        },
      },
    ],
  });

  const confirmClose = () => {
    dispatch(resetToastAlert());
    cancelConfirmation.open();
  };

  // Reset wizard to Step 1 on every mount so stale Redux step state never
  // leaves the modal stranded mid-flow on re-open.
  useEffect(() => {
    dispatch(setStepId(steps.Start));
  }, []);

  // Handle order response - track order placed event
  useEffect(() => {
    if (createCashOrderResponse) {
      onCashOrderSubmitted && onCashOrderSubmitted();
      currentStepRef.current?.onNextClick();
      trackOrderPlaced();
    }
  }, [createCashOrderResponse, onCashOrderSubmitted, trackOrderPlaced]);

  // Handle defaults error
  useEffect(() => {
    if (cashOrderDefaultsError) {
      dispatch(addToastAlert(Alert.GET_CASH_DEFAULTS_ERROR, 'error'));
    }
  }, [cashOrderDefaultsError, dispatch]);

  const orderSteps = [
    {
      stepId: steps.Start,
      title: 'Select AmpolCash',
      component: (
        <AddEditCart
          denominationList={cashOrderDefaults?.denominations}
          preloaded={cashOrderDefaults?.ampolCashTypeCode === 'PRELOADED'}
          allowedMedia={cashOrderDefaults?.allowedMedia}
          defaultPlasticTypeCode={cashOrderDefaults?.plasticTypeCode}
          defaultExpiryDate={dayjs()
            .add(cashOrderDefaults?.ampolCashExpiryDateAgreement || 36, 'months')
            .format('MM/YY')}
          onValidStateChange={setIsCartItemsValid}
          fleetLimitters={
            cashOrderDefaults?.fleetLimitters && cashOrderDefaults?.fleetLimitters.length !== 0
              ? cashOrderDefaults?.fleetLimitters?.[0].goodsCategory
              : ''
          }
        />
      ),
      onNextClick: () => {
        setFormId(deliveryOptionsFormId);
        dispatch(setStepId(steps.DeliveryOptions));
        if (!isDeliveryOptionsValid) {
          showDeliveryErrors(deliveryOption, steps.DeliveryOptions);
        }
        trackCustomEvent(
          'Continue to Delivery Options',
          loggedInUser,
          assignedAccountNumber,
          'Clicked Continue button to go to Delivery Options.'
        );
      },
    },
    {
      stepId: steps.DeliveryOptions,
      title: 'Delivery Options',
      component: (
        <DeliveryOptions
          cashOrderCartItems={cashOrderCartItems}
          cashCartSubTotal={cashCartSubTotal}
          cashOrderDefaults={cashOrderDefaults}
          selectedDeliveryOption={deliveryOption}
          formId={deliveryOptionsFormId}
          singleShippingAddress={singleShippingAddress}
          recipientsAddClick={onRecipientsAddClick}
          recipientsEditClick={onRecipientsEditClick}
          recipientsRemoveClick={onRecipientsRemoveClick}
          addEditAmpolCash={onAddEditAmpolCashClick}
          onDeliveryOptionsContinue={(isValid) => {
            const deliveryStep = orderSteps.find((s) => s.stepId === steps.DeliveryOptions);
            onDeliveryOptionsContinue(isValid, deliveryStep?.onNextClick);
          }}
          onDeliveryOptionsChange={onDeliveryOptionsChange}
        />
      ),
      onBackClick: () => {
        dispatch(setStepId(steps.Start));
        dispatch(resetToastAlert());
      },
      onNextClick: () => {
        setFormId(summaryFormId);
        dispatch(setStepId(steps.Summary));
        trackCustomEvent(
          'Continue to Summary Page',
          loggedInUser,
          assignedAccountNumber,
          'Clicked Continue button to go to Summary page on Cash Order modal.'
        );
      },
    },
    {
      stepId: steps.AddEditRecipient,
      title:
        cashOrderCartItems[cartItemUuid]?.mediaType === 'P' ? 'Recipient shipping address' : 'Recipient email address',
      component: (
        <AmpolCashRecipientForm
          formId={addEditRecipientFormId}
          cartItem={cashOrderCartItems[cartItemUuid]}
          cartItemUuid={cartItemUuid}
          recipientUuid={recipientUuid}
          isPhysical={cashOrderCartItems[cartItemUuid]?.mediaType === 'P'}
          physicalAddressRecipient={physicalAddressRecipient}
          onSubmit={onRecipientSubmit}
          recipientsEditClick={onRecipientsEditClick}
          recipientsRemoveClick={onRecipientsRemoveClick}
        />
      ),
      onBackClick: () => {
        setFormId(deliveryOptionsFormId);
        dispatch(setStepId(steps.DeliveryOptions));
      },
      onNextClick: () => {
        setFormId(deliveryOptionsFormId);
        dispatch(setStepId(steps.DeliveryOptions));
      },
    },
    {
      stepId: steps.Summary,
      title: 'Summary',
      component: (
        <OrderConfirmationSummaryPage
          cashOrderCartItems={cashOrderCartItems}
          cashCartSubTotal={cashCartSubTotal}
          cashOrderDefaults={cashOrderDefaults}
          assignedGrandfatherClientNumber={assignedGrandfatherClientNumber}
          formId={summaryFormId}
          isAdmin={isAdmin}
          onPlaceOrder={onPlaceOrder}
        />
      ),
      onBackClick: () => {
        setFormId(deliveryOptionsFormId);
        dispatch(setStepId(steps.DeliveryOptions));
      },
      onNextClick: () => dispatch(setStepId(steps.OrderPlaced)),
    },
    {
      stepId: steps.OrderPlaced,
      title: 'Order Placed',
      component: (
        <OrderConfirmationSummaryPage
          cashOrderCartItems={cashOrderCartItems}
          cashCartSubTotal={cashCartSubTotal}
          cashOrderDefaults={cashOrderDefaults}
          assignedGrandfatherClientNumber={assignedGrandfatherClientNumber}
          orderNumber={createCashOrderResponse?.orderNumber}
          isAdmin={isAdmin}
        />
      ),
      onNextClick: confirmClose,
    },
  ];

  const currentStep = orderSteps.find((step) => step.stepId === stepId) || orderSteps[0];
  currentStepRef.current = currentStep;

  const getActionButtons = () => {
    return [
      ...(currentStep.stepId === steps.Start
        ? [
            {
              label: 'Continue',
              onClick: hasCartItems && isCartItemsValid ? currentStep.onNextClick : null,
              inactive: !hasCartItems || !isCartItemsValid,
            },
          ]
        : []),
      ...(currentStep.stepId === steps.DeliveryOptions
        ? [
            {
              label: 'Continue',
              type: deliveryOption === 'optionTwo' ? 'submit' : 'button',
              onClick:
                deliveryOption !== 'optionTwo'
                  ? () => onDeliveryOptionsContinue(isDeliveryOptionsValid, currentStep.onNextClick)
                  : null,
            },
          ]
        : []),
      ...(currentStep.stepId === steps.AddEditRecipient
        ? [
            {
              label: 'Continue',
              type: 'submit',
            },
          ]
        : []),
      ...(currentStep.stepId === steps.Summary
        ? [
            {
              label: 'Place order',
              type: 'submit',
              onClick: !isAdmin ? onPlaceOrder : undefined,
            },
          ]
        : []),
      ...(currentStep.stepId === steps.OrderPlaced
        ? [
            {
              label: 'Close',
              onClick: closeModal,
            },
          ]
        : []),
      ...(currentStep.stepId !== steps.Start
        ? [
            {
              label: 'Back',
              onClick: currentStep.onBackClick,
            },
          ]
        : []),
      {
        label: 'Cancel Order',
        onClick: hasCartItems ? confirmClose : closeModal,
        color: 'black',
        variant: ButtonVariant.Tertiary,
      },
    ];
  };

  const closeActionButton = [
    {
      label: 'Close',
      onClick: closeModal,
    },
  ];

  const modalProps = {
    id: 'order-cash',
    formId: formId,
    title: currentStep.title,
    subtitle: `AmpolCash for Account ${assignedGrandfatherClientNumber}`,
    content: <ModalContentWrapper currentStep={currentStep} variant='triple' />,
    variant: 'triple',
    onClose: confirmClose,
    preCloseFn: hasCartItems && currentStep.stepId !== steps.OrderPlaced ? () => cancelConfirmation.open() : closeModal,
    manualClose: true,
    actions: stepId === steps.OrderPlaced ? closeActionButton : getActionButtons(),
    color: 'blue',
  };

  const orderCashModal = useModal(modalProps);

  useEffect(() => {
    if (!(plasticTypesListLoading || cashOrderDefaultsLoading) && !(plasticTypesListError || cashOrderDefaultsError)) {
      orderCashModal.open();
    }
  }, [plasticTypesListLoading, cashOrderDefaultsLoading]);

  return <>{(plasticTypesListLoading || cashOrderDefaultsLoading || createCashOrderLoading) && <PageLoader />}</>;
};

export default AmpolCashOrderModal;
